/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import { StoreQueryParams } from '../../../../ts';
import { Action } from '../../../../ts/action';
import { getAccessPrefixedUrl } from '../../../ia/util/access-control';
import { endpoints } from '../../../ia/util/config';
import validation from '../../../ia/util/validation';

const actionsEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/actions`
    )
  );

const actionEndpoint = (
  skillId: string,
  actionId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  `${actionsEndpoint(skillId, useNewIAUpdatedAccess, assistantId)}/${actionId}`;

const duplicateEndpoint = (
  skillId: string,
  actionId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    actionEndpoint(skillId, actionId, useNewIAUpdatedAccess, assistantId),
    'duplicate'
  );

const actionsImportEndpoint = (
  assistantId: string,
  skillId: string,
  useNewIAUpdatedAccess: boolean
): string =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/actions_import`
    )
  );

export const strings = {
  ACTION_NOT_FOUND: 'Action does not exist',
  ACTION_ALREADY_EXISTS: 'Action cannot be created as it already exists'
};

const validateAll = skillId => {
  var validationErrors = [];

  if (skillId) {
    const skillErrors = validation.validateGuid(skillId);
    if (skillErrors) {
      validationErrors.push(skillErrors);
    }
  }

  // commented out until exists and not exists are supported
  // if (action) {
  //   const actionErrors = validation.validateAction(action);
  //   if (actionErrors) {
  //     validationErrors.push(actionErrors);
  //   }
  // }

  if (validationErrors.length) {
    return Promise.reject({ data: { error: validationErrors } });
  }

  return null;
};

export async function deleteAction(
  skillId: string,
  actionId: string,
  options: any,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  const params = {
    ...options
  };

  const config = { params };

  return axios
    .delete(
      actionEndpoint(skillId, actionId, useNewIAUpdatedAccess, assistantId),
      config
    )
    .then(response => response)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

export async function getAction(
  skillId: string,
  actionId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  options: StoreQueryParams = {}
) {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  const params = {
    export: true,
    include_audit: true,
    include_invalid_refs_count: true,
    include_sub_action_refs: true,
    ...options
  };

  const config = { params };

  return axios
    .get(
      actionEndpoint(skillId, actionId, useNewIAUpdatedAccess, assistantId),
      config
    )
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

export async function listActions(
  skillId: string,
  options: any,
  reactivateSession: any,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  const params = {
    page_limit: 3000,
    include_count: true,
    include_audit: true,
    sort: 'action',
    include_invalid_refs_count: true,
    include_variables: true,
    include_condition: true,
    ...options
  };

  const config = { params };

  return axios
    .get(
      urlJoin(actionsEndpoint(skillId, useNewIAUpdatedAccess, assistantId), {
        query: { reactivateSession }
      }),
      config
    )
    .then(response => {
      return {
        actions: response.data.actions,
        total: response.data.pagination.total,
        nextURL: response.data.pagination.next_url
          ? response.data.pagination.next_url
          : ''
      };
    })
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

export async function updateAction(
  skillId: string,
  actionId: string,
  newAction: any,
  options: any,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  // if validateAll updates to use action param, pass here using
  // const validationErrors = validateAll(skillId, newAction);
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }
  const params = {
    ...options
  };

  const config = { params };

  return axios
    .post(
      actionEndpoint(skillId, actionId, useNewIAUpdatedAccess, assistantId),
      newAction,
      config
    )
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

export const duplicateAction = async (
  skillId: string,
  actionId: string,
  data: any,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) => {
  const url = duplicateEndpoint(
    skillId,
    actionId,
    useNewIAUpdatedAccess,
    assistantId
  );
  try {
    const response = await axios.post(url, data);
    return response.data;
  } catch (err) {
    return await Promise.reject(_.get(err, 'response.data') || err);
  }
};

export async function importActions(
  skillId: string,
  actions: { actions: Action[] },
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  options?: any
) {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  let config;
  if (options) {
    config = { params: { ...options } };
  }

  return axios
    .post(
      actionsImportEndpoint(assistantId, skillId, useNewIAUpdatedAccess),
      actions,
      config
    )
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

export async function importActionsStatus(
  skillId: string,
  taskId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  return axios
    .get(
      actionsImportEndpoint(assistantId, skillId, useNewIAUpdatedAccess) +
        `/${taskId}`
    )
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

export const baseLLMModelsEndpoint = (): string =>
  `${endpoints.store}/v2/tooling/watsonx/models?task=`;

export const getBaseLLMModelData = (taskId: string): any =>
  axios.get(baseLLMModelsEndpoint() + `${taskId}`);
